import { useEffect, useState } from 'react';
import { isInIframe, urls } from '@zapier/identity-utils';
import { addTruthyParamsToUrl } from '@zapier/url-utils';
import { useTrackingContext } from '@zapier/toolbox-tracking';

import { fetch, logout } from '../../utils';
import {
  GENERIC_ERROR_MESSAGE,
  SIGNUP_USE_DIFFERENT_EMAIL,
  ZAPIER_USER_CREATED_EVENT_PROVISIONING_METHOD_PARTNER_QUAC,
} from './constants';
import {
  SignupData,
  SignupErrors,
  SignupFieldFlags,
  SignupOutput,
  SignupResult,
  SignupResultType,
  SignupStatus,
  UseSignupInput,
} from './types';
import { parseSignupErrors } from './errorUtils';
import { SSOOption } from '../../types';
import { storeAuthType } from '../useAuthType';
import {
  discoverDomain,
  DiscoveredDomain,
  DiscoveredDomainAccount,
} from '../../utils/discoverDomain';
import { captureException } from '@sentry/react';
import { dispatchUserInteractionEvent } from '../../avro';
import isEmpty from 'lodash/isEmpty';
import { useForm, SubmitHandler } from 'react-hook-form';

const defaultState = {
  email: '',
  firstName: '',
  lastName: '',
  password: '',
};

const defaultFlagState = {
  captcha: false,
  email: false,
  firstName: false,
  lastName: false,
  password: false,
};

function defaultRouteTo(url: string) {
  window.location.href = url;
}

async function signup(
  data: SignupData,
  next: string | undefined,
  selectedApis: string | string[] | undefined,
  routeTo: (url: string) => void
): Promise<SignupOutput> {
  // First ensure we are logged out
  await logout();

  const body: Record<string, any> = {
    captcha: data.captcha,
    email: data.email,
    first_name: data.firstName,
    last_name: data.lastName,
    // API requires this field to be duplicated
    password: data.password,
    password1: data.password,
    selected_apis: selectedApis,
  };

  if (data.invitation) {
    body.invitation = data.invitation;
  }

  if (
    data.userProvisioningMethod ===
    ZAPIER_USER_CREATED_EVENT_PROVISIONING_METHOD_PARTNER_QUAC
  ) {
    body.user_provisioning_method =
      ZAPIER_USER_CREATED_EVENT_PROVISIONING_METHOD_PARTNER_QUAC;
  }

  // Attempt to sign user up
  const signupResult = await fetch<SignupResult>(urls.SIGNUP_API_URL, {
    method: 'POST',
    body,
  });

  // Errors are in a hard to read format so reformat them
  // for easy consumption in the UI
  const errors = parseSignupErrors(signupResult);

  // See if we need to redirect the user
  if (errors.redirectUrl) {
    // The verify-email page requires the user's email as a query param, so
    // thread it through only for that redirect. addTruthyParamsToUrl drops
    // falsy values, leaving every other redirect unchanged.
    const params: Record<string, string | undefined> = { next };
    if (errors.type === SignupResultType.IS_EMAIL_VERIFICATION_REQUIRED) {
      params.email = data.email;
    }

    routeTo(addTruthyParamsToUrl(errors.redirectUrl, params));
  }

  // If there were no errors, and we successfully signed up, store the user's authentication method, so we can automatically
  // show this authentication method next time they try to log in.
  if (Object.keys(errors.errors || {}).length === 0) {
    storeAuthType('email');
  }

  return {
    didRedirect: Boolean(errors.redirectUrl),
    userId: signupResult?.body?.id,
    ...errors,
  };
}

function getDefaultState(input: UseSignupInput) {
  return {
    ...defaultState,
    email: input.email || defaultState.email,
    firstName: input.firstName || defaultState.firstName,
    lastName: input.lastName || defaultState.lastName,
    invitation: input.invitation,
    userProvisioningMethod: input.metadata?.userProvisioningMethod,
    skipOnboarding: input.metadata?.skipOnboarding,
  };
}

function shouldShowPassword(
  formData: SignupData,
  formErrors: SignupErrors,
  status: SignupStatus,
  submittedFields: SignupFieldFlags,
  touchedFields: SignupFieldFlags
) {
  // If the user has submitted the email
  if (submittedFields.email && status === 'initial') return true;

  // There is an error (but not with the email field)
  if (
    status === 'error' &&
    !formErrors.email &&
    !formErrors.firstName &&
    !formErrors.lastName &&
    formErrors.password
  )
    return true;

  // The user has submitted a password
  if (status === 'submitting' && touchedFields.password) return true;

  // This catches password managers where the user auto-fills
  // the email and password. We can't use formData.password
  // in case the user deletes the password field completely
  // we don't want it flipping back to the email
  if (formData.email && touchedFields.password) return true;

  return false;
}

/**
 * React hook that controls the data / flow of signing a user up for Zapier
 */
export function useSignup(input: UseSignupInput = {}) {
  const [status, setStatus] = useState<SignupStatus>('initial');
  const [hasTouched, setHasTouched] = useState(false);
  const [formErrors, setErrors] = useState<SignupErrors>({});
  const [submittedFields, setSubmittedFields] = useState<SignupFieldFlags>({
    ...defaultFlagState,
  });
  const [discoveredDomainAccount, setDiscoveredDomainAccount] = useState<
    DiscoveredDomainAccount | null | undefined
  >(input.discoveredDomainAccount);

  const formMethods = useForm<SignupData>({
    defaultValues: getDefaultState(input),
    mode: 'onChange',
  });

  const {
    clearErrors,
    reset: resetForm,
    handleSubmit: handleSubmitForm,
    watch,
    setValue,
    formState: { touchedFields },
  } = formMethods;

  const formData = watch();
  const { trackSignupIntent } = useTrackingContext();

  const isCaptchaRequired = Boolean(formErrors.captcha);

  const routeTo = input.routeTo || defaultRouteTo;

  const shouldShowPasswordField = shouldShowPassword(
    formData,
    formErrors,
    status,
    submittedFields,
    touchedFields as SignupFieldFlags
  );
  const shouldShowEmailField = !shouldShowPasswordField;
  const shouldShowNameFields =
    (hasTouched || !input.hideFieldsUntilFocus) && !shouldShowPasswordField;

  const shouldShowGoogleSSO = shouldShowSSOOption(
    'google',
    input.googleSSOEnabled
  );

  useEffect(() => {
    // force the input email if the input changes
    if (input.email) {
      setValue('email', input.email || '');
    }
  }, [input.email, setValue]);

  useEffect(() => {
    // If the user entered the captcha on the last handleChange
    // then go ahead and submit the form since there's no
    // submit button visible
    if (formData.captcha) {
      submitForm(formData);
    }
    // Adding the handleSubmit would cause this to run on every
    // rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.captcha]);

  function handleClearForm() {
    resetForm(getDefaultState(input));
    clearErrors();
    setErrors({});
    setSubmittedFields({
      ...defaultFlagState,
    });
    setStatus('initial');
  }

  function handleFocus() {
    if (!hasTouched) {
      trackSignupIntent('direct' as any, {
        selected_apis: input.selectedApis,
      });
    }

    setHasTouched(true);
  }

  const submitForm: SubmitHandler<SignupData> = async formData => {
    // Show that we are submitting
    setStatus('submitting');
    setSubmittedFields({
      captcha: Boolean(formData.captcha),
      email: Boolean(formData.email),
      firstName: Boolean(formData.firstName),
      lastName: Boolean(formData.lastName),
      password: Boolean(formData.password),
    });

    try {
      // domain discovery
      if (shouldShowEmailField && !formData.invitation) {
        const discoverDomainResult = await discoverDomain({
          email: formData.email,
        });

        /** to be eligible for Domain Capture,
         *  the result must have
         *   - no errors
         *   - an account with an id
         *   - no user[id]
         */
        if (
          discoverDomainResult?.errors == null &&
          discoverDomainResult?.account?.id != null &&
          discoverDomainResult?.user?.id == null
        ) {
          setDiscoveredDomainAccount(discoverDomainResult.account);

          // domain account found, stop processing the signup
          return;
        } else {
          if (hasErrors(discoverDomainResult)) {
            // stop processing the signup
            return;
          }

          setDiscoveredDomainAccount(null);
        }
      }

      // Attempt signup
      const signupResult = await signup(
        formData,
        input.next,
        input.selectedApis,
        routeTo
      );

      // Bail if we had to redirect and allow the redirect
      // to happen
      if (signupResult.didRedirect) return;

      if (Object.keys(signupResult?.errors || {}).length > 0) {
        // We encountered errors and couldn't log in
        setErrors(signupResult?.errors || {});
        setStatus('error');
      } else {
        // Success
        setErrors({});
        setStatus('success');
        const nextWithParams = addTruthyParamsToUrl(
          input.next || urls.DEFAULT_SIGNUP_NEXT,
          {
            selected_apis: input.selectedApis,
            from: input.from,
            invitation_code: formData.invitation,
          }
        );
        // Route the user to the next location or the dashboard
        routeTo(nextWithParams);
      }
    } catch (err) {
      // This will catch things like network errors and the server being down
      captureException(err);
      setErrors({ error: GENERIC_ERROR_MESSAGE });
      setStatus('error');
    }
  };

  function hasErrors(discoverDomainResult: DiscoveredDomain | null): boolean {
    const errors = discoverDomainResult?.errors;
    if (errors != null) {
      // only include errors which are strings or objects containing "message"
      const errorString = parseDomainCaptureErrors(errors);

      if (isEmpty(errorString)) {
        throw Error(
          'Error occurred parsing discoverDomainResult.errors: ' + errors
        );
      }

      setErrors({ email: errorString });
      setStatus('error');

      return true;
    }
    return false;
  }

  const onClickJoinTeam = async () => {
    void dispatchUserInteractionEvent({
      destination_url_address: discoveredDomainAccount?.ssoUrl,
      event_action: 'click',
      option_selected: 'DOMAIN_DISCOVERY_join_team',
      interaction_name: 'DOMAIN_DISCOVERY_join_team',
      interaction_raw_text: 'Join team Button Clicked',
      interaction_goal: 'Redirect to the Domain Capture SSO Url',
    });

    if (discoveredDomainAccount?.ssoUrl != null) {
      await logout();

      routeTo(
        addTruthyParamsToUrl(discoveredDomainAccount.ssoUrl, {
          is_domain_capture_signup: 'true',
          next: input.next,
          skip_onboarding: input.metadata?.skipOnboarding,
          user_provisioning_method: input.metadata?.userProvisioningMethod,
        })
      );
    } else {
      setDiscoveredDomainAccount(null);
      setErrors({ error: GENERIC_ERROR_MESSAGE });
      setStatus('error');
    }
  };

  const onClickUseDifferentEmail = () => {
    void dispatchUserInteractionEvent({
      event_action: 'click',
      option_selected: 'DOMAIN_DISCOVERY_use_different_email',
      interaction_name: 'DOMAIN_DISCOVERY_use_different_email',
      interaction_raw_text: 'Use different email Button clicked',
      interaction_goal:
        'Close the modal to enter a different email address in the sign-up form',
    });

    // clear domain info
    setDiscoveredDomainAccount(null);

    // reset page if from sso
    if (input.isFromSsoReturn) {
      setStatus('initial');
      routeTo(urls.SIGNUP_URL);
    } else {
      // clear email address
      setValue('email', '');
      setErrors({ email: SIGNUP_USE_DIFFERENT_EMAIL });
      setStatus('error');
    }
  };

  const onClickGoToHomepage = () => {
    void dispatchUserInteractionEvent({
      event_action: 'click',
      option_selected: 'DOMAIN_DISCOVERY_go_to_homepage',
      interaction_name: 'DOMAIN_DISCOVERY_go_to_homepage',
      interaction_raw_text: 'Go to homepage Button clicked',
      interaction_goal: 'Close the modal and redirect to the homepage',
    });

    routeTo(urls.INDEX_URL);
  };

  return {
    // Current field data in the form
    formData,
    // Current errors in the form including field level errors and more generic form-level errors
    formErrors,
    // All methods used for react-hook-form
    formMethods,
    // Callback to handle clearing the form and resetting to default state
    handleClearForm,
    // Callback to handle when a field is focused
    handleFocus,
    // Callback to handle submitting form data to API
    handleSubmit: handleSubmitForm(submitForm),
    // Will be true if the user has focused and fields in the form
    hasTouched,
    // Captcha input is required when true
    isCaptchaRequired,
    // Should show the email field be shown in the email/password form
    shouldShowEmailField,
    // Should show the first / last name fields be shown in the email/password form
    shouldShowNameFields,
    // Should show the password field be shown in the email/password form
    shouldShowPasswordField,
    // Should Google SSO button be shown
    shouldShowGoogleSSO,
    // Form status
    status,
    // Fields that have been submitted to the API
    submittedFields,
    // domain discovery: details, if applicable
    discoveredDomainAccount,
    // domain discovery: handle when user chooses not to join their company account
    onClickUseDifferentEmail,
    // domain discovery: handle when user chooses to join their company account
    onClickJoinTeam,
    // domain discovery: handle when user chooses to go to the homepage
    onClickGoToHomepage,
  };
}

function shouldShowSSOOption(
  expectedSSOOption: SSOOption,
  ssoOptionEnabled?: boolean
) {
  // If we're in an iframe, Facebook is the only one that works.
  if (isInIframe() && expectedSSOOption !== 'facebook') {
    return false;
  }

  return ssoOptionEnabled === undefined || ssoOptionEnabled === true;
}

export function parseDomainCaptureErrors(errors: any[] | null | undefined) {
  if (errors == null) return '';

  return errors
    .map(item =>
      typeof item == 'string' ? item : typeof item == 'object' && item.message
    )
    .filter(item => item != null)
    .join(', ');
}
